import { sortByOptionsMap } from 'Components/Account/AccountPage.helpers';
import type { VehicleListQuery } from 'Components/Account/AccountPage.types';
import type { UseAccountRouterReturnType } from 'Components/Account/useAccountRouter';
import useAccountRouter from 'Components/Account/useAccountRouter';
import cypressIds from 'Components/cypress_ids.json';
import { DownloadCSV } from 'Components/DownloadCSV/DownloadCSV';
import {
	triggerCompletePurchasesDownloadCSVAbortedEvent,
	triggerCompletePurchasesDownloadCSVClickedEvent,
	triggerCompletePurchasesDownloadCSVFailedEvent,
	triggerCompletePurchasesDownloadCSVHoveredEvent,
	triggerCompletePurchasesDownloadCSVSuccessEvent,
} from 'Services/analytics/events';
import { generatePurchasesCsv, getPurchasesCsv } from 'Services/api/bids';
import { AccountDateRange } from 'Types/account';
import { PurchasesCompleteStatusTypes } from 'Types/purchaseCompleteStatus';
import { getAccountDateRange } from 'Utilities/helpers/getAccountDateRange';

type PurchasesDownloadCsvProps = { isDisabled?: boolean; queryStatus: VehicleListQuery['queryStatus'] };

const generateCsv = async (accountParams: UseAccountRouterReturnType) => {
	const { hideDelivered, keyword, order, purchasesCompleteStatusType, sortBy } = accountParams;
	const dateRangeType = accountParams.dateRangeType || AccountDateRange.ALL;
	const { from: dateFrom, to: dateTo } = getAccountDateRange(dateRangeType);

	await generatePurchasesCsv({
		dateFrom,
		dateTo,
		hideDeliveredEnquiries: hideDelivered,
		keyword,
		order,
		sortBy: sortBy ? sortByOptionsMap[sortBy] : undefined,
		status: purchasesCompleteStatusType || PurchasesCompleteStatusTypes.All,
	});
};

export const PurchasesDownloadCsv = ({ isDisabled, queryStatus }: PurchasesDownloadCsvProps): JSX.Element => {
	const accountParams = useAccountRouter();

	const pollingProps = {
		fetchCsv: getPurchasesCsv,
		filenameTitle: 'Motorway purchases',
		generateCsv: () => generateCsv(accountParams),
		queryKey: ['getPurchasesCsvPolling'],
	};

	const handlers = {
		onDownloadButtonClick: triggerCompletePurchasesDownloadCSVClickedEvent,
		onDownloadButtonHover: triggerCompletePurchasesDownloadCSVHoveredEvent,
		onPollingAborted: triggerCompletePurchasesDownloadCSVAbortedEvent,
		onPollingError: triggerCompletePurchasesDownloadCSVFailedEvent,
		onPollingSuccess: triggerCompletePurchasesDownloadCSVSuccessEvent,
	};

	return (
		<DownloadCSV
			cypressId={cypressIds.buttons.purchasesCompleteDownload}
			handlers={handlers}
			isDisabled={isDisabled}
			pollingProps={pollingProps}
			queryStatus={queryStatus}
		/>
	);
};
